import moment from 'moment';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import type { Day, SelectedDate } from '../../types';

/**
 * Port of `commonComponents/dayCard.jsx` — one day cell in the week strip.
 *
 * The class-name template literals are reproduced exactly, whitespace included: the
 * original's template spans several lines, so the emitted className contains real
 * newlines and runs of spaces between the holiday and leave segments. The browser
 * collapses them when matching, but the attribute value differs from a tidied version,
 * and App.css is carried byte-identical — so this is left alone rather than reformatted.
 *
 * Note "N" is a class name the original emits for the no-holiday and no-leave cases; it
 * is not a placeholder.
 */
export function DayCard({
  day,
  selectedDate,
}: {
  day?: Day;
  selectedDate: SelectedDate;
}): JSX.Element {
  /** Recolours the tooltip on entry — the original mutates the DOM node directly. */
  const entering = (element: HTMLElement): void => {
    const inner = element.children[1] as HTMLElement | undefined;
    if (!inner) return;
    inner.style.backgroundColor = '#2b7bcc';
    inner.style.color = '#fff';
    inner.style.borderColor = '#2b7bcc';
  };

  const hourlyCount = day?.hourlyLeaveCount || 0;

  const statusTooltip = (
    <>
      {day?.isLeave !== 'N' && (
        <>
          <strong>Leave Status: </strong>{' '}
          <span>
            {day?.isLeave === 'Leave - Full Day'
              ? 'Full-Day Leave'
              : day?.isLeave === 'Leave - Half Day'
                ? 'Half-Day Leave'
                : day?.isLeave === 'Leave - Half Day/Half Day'
                ? 'Two Half-Day Leaves'
                : day?.isLeave === 'Leave - Hourly'
                  ? hourlyCount > 1
                    ? 'Hourly Leaves'
                    : 'Hourly Leave'
                  : ''}
          </span>
          <br></br>
        </>
      )}
      {day?.isHoliday !== 'N' && (
        <>
          <strong>Holiday Status: </strong>{' '}
          <span>
            {day?.isHoliday === 'Holiday - Full Day'
              ? 'Full-Day Holiday'
              : day?.isHoliday === 'Holiday - Half Day'
                ? 'Half-Day Holiday'
                : ''}
          </span>
        </>
      )}
    </>
  );

  const dayCardInput = (
    <div
      className={`week-number ${day?.date === selectedDate.today ? 'today' : ''} ${
        day?.isHoliday === 'Holiday - Full Day'
          ? 'Holiday_Full_Day'
          : day?.isHoliday === 'Holiday - Half Day'
            ? 'Holiday_Half_Day'
            : 'N'
      }
    ${
      day?.isLeave === 'Leave - Full Day'
        ? 'Leave_Full_Day'
        : day?.isLeave === 'Leave - Half Day'
          ? 'Leave_Half_Day'
        : day?.isLeave === 'Leave - Half Day/Half Day'
        ? 'Leave_Half_Day'
          : day?.isLeave === 'Leave - Hourly'
            ? 'Leave_Hourly'
            : 'N'
    }`}
    >
      <div className="date"> {moment(day?.date).format('D')}</div>
      <div className="day">{moment(day?.date).format('dddd')}</div>
      <div className="leave-status"></div>
    </div>
  );

  return (
    <>
      <div
        className="ch-week-col"
        tabIndex={0}
        title={`${moment(day?.date).format('D')} ${moment(day?.date).format(
          'dddd',
        )} total hours per day are  ${day?.timeTotal} Hours`}
        aria-label={`${moment(day?.date).format('D')} ${moment(day?.date).format('dddd')} ${
          day?.timeTotal
        } Hours`}
      >
        {day?.isHoliday !== 'N' || day?.isLeave !== 'N' ? (
          <OverlayTrigger
            placement="top"
            trigger={['hover', 'focus']}
            overlay={
              <Tooltip id={`tooltip`} className="leaveHoliday_status_tooltip">
                {statusTooltip}
              </Tooltip>
            }
            onEntering={entering}
          >
            {dayCardInput}
          </OverlayTrigger>
        ) : (
          dayCardInput
        )}
        <div className={`week-hour ${day?.timeError ? 'active' : ''}`}>{day?.timeTotal} Hrs</div>
      </div>
    </>
  );
}
